import React, { useEffect, useState } from "react";

type SubjectAreaFieldProps = {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  onAddSubjectArea: (value: string) => Promise<{ ok: boolean; value: string; error?: string }>;
  disabled?: boolean;
  emptyLabel?: string;
};

export function SubjectAreaField({
  value,
  options,
  onChange,
  onAddSubjectArea,
  disabled = false,
  emptyLabel = "Select subject area"
}: SubjectAreaFieldProps): JSX.Element {
  const [isAdding, setIsAdding] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAdding) {
      setDraftValue("");
      setError(null);
    }
  }, [isAdding]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const result = await onAddSubjectArea(draftValue);
    setIsSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? "Unable to add subject area.");
      return;
    }
    onChange(result.value);
    setIsAdding(false);
  };

  return (
    <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
      <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
        Subject area
      </label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-border-[#0f172a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#0f172a]/10 disabled:tw-opacity-60"
        disabled={disabled}
      >
        <option value="">{options.length ? emptyLabel : "No subject areas available"}</option>
        {options.map((subjectArea) => (
          <option key={subjectArea} value={subjectArea}>
            {subjectArea}
          </option>
        ))}
      </select>
      {isAdding ? (
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
          <input
            type="text"
            value={draftValue}
            onChange={(event) => {
              setDraftValue(event.target.value);
              if (error) {
                setError(null);
              }
            }}
            placeholder="Enter new subject area"
            className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-border-[#0f172a] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#0f172a]/10"
            disabled={isSubmitting}
          />
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#03234a] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-bg-slate-300"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 hover:tw-border-slate-400 hover:tw-text-slate-800"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
          {error ? <p className="tw-text-xs tw-text-red-600">{error}</p> : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="tw-mt-2 tw-inline-flex tw-self-start tw-items-center tw-gap-1 tw-appearance-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[13px] tw-font-semibold tw-text-[#03234a] tw-underline tw-decoration-[#03234a]/35 tw-underline-offset-2 hover:tw-text-[#021b39] hover:tw-decoration-[#021b39] focus:tw-outline-none"
        >
          <span aria-hidden="true" className="tw-text-[15px] tw-leading-none">+</span>
          Add subject area
        </button>
      )}
    </div>
  );
}
